import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { AuthUser, LoginCredentials, RegisterData, ForgotPasswordData, ResetPasswordData, AuthContextType } from '../types';
import { authApi } from '../utils/api';

// Real API authentication functions
const loginApi = async (credentials: LoginCredentials): Promise<AuthUser | null> => {
  try {
    const response = await authApi.login(credentials.email, credentials.password);
    
    if (response.error) {
      throw new Error(response.message);
    }

    return {
      id: response.data.id,
      email: response.data.email,
      admin_type: response.data.admin_type,
      token: response.data.token,
      token_expiry: response.data.token_expiry,
      refresh_token: response.data.refresh_token
    };
  } catch (error) {
    console.error('Login API error:', error);
    throw error;
  }
};

const mockRegister = async (data: RegisterData): Promise<AuthUser | null> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Mock validation
  if (data.password !== data.confirmPassword) {
    throw new Error('Passwords do not match');
  }
  
  if (data.password.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }
  
  // Simulate successful registration
  return {
    id: Math.random().toString(36).substr(2, 9),
    email: data.email,
    admin_type: data.role || 'user',
    token: 'mock-token',
    token_expiry: Date.now() + 24 * 60 * 60 * 1000,
    refresh_token: 'mock-refresh-token'
  };
};

const mockForgotPassword = async (data: ForgotPasswordData): Promise<boolean> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Mock validation - just check if email format is valid
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(data.email);
};

const mockResetPassword = async (data: ResetPasswordData): Promise<boolean> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Mock validation
  if (data.password !== data.confirmPassword) {
    throw new Error('Passwords do not match');
  }
  
  if (data.password.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }
  
  return true;
};

// Create Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored auth on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const accessValid = parsedUser.token_expiry > Date.now() / 1000;
        const hasRefresh = Boolean(parsedUser.refresh_token);
        if (accessValid || hasRefresh) {
          setUser(parsedUser);
          localStorage.setItem('token', parsedUser.token);
        } else {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      } catch (error) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setIsLoading(false);
  }, []);

  // Sync React state when api layer refreshes tokens; clear on hard session failure
  useEffect(() => {
    const onTokensRefreshed = () => {
      const raw = localStorage.getItem('user');
      if (!raw) return;
      try {
        setUser(JSON.parse(raw));
      } catch {
        // ignore
      }
    };
    const onSessionExpired = () => {
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    };
    window.addEventListener('auth:tokens-refreshed', onTokensRefreshed);
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => {
      window.removeEventListener('auth:tokens-refreshed', onTokensRefreshed);
      window.removeEventListener('auth:session-expired', onSessionExpired);
    };
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true);
    try {
      const authUser = await loginApi(credentials);
      if (authUser) {
        setUser(authUser);
        // if (credentials.rememberMe) {
          localStorage.setItem('user', JSON.stringify(authUser));
        // }
        // Store token separately for API requests
        localStorage.setItem('token', authUser.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    setIsLoading(true);
    try {
      const authUser = await mockRegister(data);
      if (authUser) {
        setUser(authUser);
        localStorage.setItem('user', JSON.stringify(authUser));
        localStorage.setItem('token', authUser.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const forgotPassword = async (data: ForgotPasswordData): Promise<boolean> => {
    try {
      return await mockForgotPassword(data);
    } catch (error) {
      console.error('Forgot password error:', error);
      return false;
    }
  };

  const resetPassword = async (data: ResetPasswordData): Promise<boolean> => {
    try {
      return await mockResetPassword(data);
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
